import { supabase } from '../lib/supabase';
import { storageService } from './storageService';
import { menuService } from './menuService';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const catalogService = {
  // Get catalog by ID (public access for published catalogs)
  async getCatalogById(catalogId) {
    try {
      const { data: catalog, error: catalogError } = await supabase?.from('catalogs')?.select(`
          *,
          catalogPages:catalog_pages(
            id,
            page_number,
            image_url,
            image_alt,
            thumbnail_url,
            thumbnail_alt,
            width,
            height,
            image_path,
            public_image_path,
            keywords
          ),
          company:companies(vtex_account_name, logo_url)
        `)?.eq('id', catalogId)?.order('page_number', { foreignTable: 'catalog_pages', ascending: true })?.maybeSingle();

      if (catalogError) throw catalogError;
      
      // If no catalog found, return null instead of throwing error
      if (!catalog) {
        return null;
      }

      // Fetch hotspots separately with public access
      const { data: hotspots, error: hotspotsError } = await supabase?.from('hotspots')?.select('*')?.eq('catalog_id', catalogId)?.eq('visible', true);

      if (hotspotsError && hotspotsError?.code !== 'PGRST116') {
        console.warn('Error fetching hotspots:', hotspotsError);
      }

      // For published catalogs, use public URLs; for drafts, use signed URLs
      const isPublished = catalog?.status === 'published';
      
      console.log(`[GET_CATALOG] Loading catalog ${catalogId}, status: ${catalog?.status}, published: ${isPublished}`);
      console.log(`[GET_CATALOG] Total pages: ${catalog?.catalogPages?.length}`);
      
      // Check if published catalog needs migration (missing public images)
      if (isPublished) {
        const pagesNeedingMigration = catalog?.catalogPages?.filter(
          page => !page?.public_image_path && page?.image_path
        );
        
        if (pagesNeedingMigration?.length > 0) {
          console.warn(`[GET_CATALOG] Published catalog has ${pagesNeedingMigration?.length} pages without public images`);
          console.log('[GET_CATALOG] Triggering automatic migration...');
          
          // Trigger migration in background (don't await to avoid blocking)
          this.migratePublishedCatalogImages(catalogId, catalog?.company_id, pagesNeedingMigration)?.then(() => console.log('[GET_CATALOG] Migration completed successfully'))?.catch(err => console.error('[GET_CATALOG] Migration failed:', err));
        }
      }
      
      const pagesWithUrls = await Promise.all(
        (catalog?.catalogPages || [])?.map(async (page) => {
          let imageUrl = page?.image_url;

          if (isPublished) {
            if (page?.public_image_path) {
              // Use public URL for published catalogs (works in embed contexts)
              imageUrl = storageService?.getPublicCatalogImageUrl(page?.public_image_path);
              console.log(`[GET_CATALOG] Page ${page?.page_number}: Using public URL`);
            } else if (page?.image_path) {
              // Fallback: Try to generate signed URL if public image missing
              console.warn(`[GET_CATALOG] Page ${page?.page_number}: Missing public_image_path, falling back to signed URL`);
              try {
                imageUrl = await storageService?.getCatalogFileUrl(page?.image_path, 7200);
              } catch (err) {
                console.error(`[GET_CATALOG] Error generating signed URL for page ${page?.page_number}:`, err?.message);
                imageUrl = null;
              }
            } else {
              console.error(`[GET_CATALOG] Page ${page?.page_number}: No image_path or public_image_path available`);
              imageUrl = null;
            }
          } else {
            // Draft catalog: use signed URLs
            if (page?.image_path) {
              try {
                imageUrl = await storageService?.getCatalogFileUrl(page?.image_path, 7200);
              } catch (err) {
                console.error(`[GET_CATALOG] Error generating signed URL for page ${page?.page_number}:`, err?.message);
                imageUrl = null;
              }
            }
          }

          return {
            ...page,
            imageUrl
          };
        })
      );
      
      // Log validation results
      const pagesWithImages = pagesWithUrls?.filter(p => p?.imageUrl)?.length;
      const pagesWithoutImages = pagesWithUrls?.filter(p => !p?.imageUrl)?.length;
      console.log(`[GET_CATALOG] Pages with valid URLs: ${pagesWithImages}/${pagesWithUrls?.length}`);
      if (pagesWithoutImages > 0) {
        console.error(`[GET_CATALOG] ${pagesWithoutImages} pages have no valid image URLs`);
      }

      // Load navigation menu tree with per-catalog page mappings
      const menuTree = await menuService.getMenuWithMappings(
        catalog?.company_id,
        catalogId
      );

      return toCamelCase({
        ...catalog,
        catalogPages: pagesWithUrls,
        hotspots: hotspots || [],
        menuTree
      });
    } catch (error) {
      console.error('[GET_CATALOG] Error fetching catalog:', error);
      throw error;
    }
  },

  /**
   * BUSINESS RULE: Internal App Viewer - Supports BOTH draft and published catalogs
   * 
   * Used by: catalog-viewer (authenticated users viewing catalogs inside the app)
   * 
   * Access Control:
   * - PUBLISHED catalogs: Accessible to anyone (public)
   * - DRAFT catalogs: Requires authentication + company membership (owner/team)
   * - ARCHIVED catalogs: Requires authentication + company membership
   * 
   * This allows internal users to:
   * - Preview draft catalogs before publishing
   * - Review and edit catalog content
   * - Validate hotspot placement and functionality
   * 
   * @param {string} catalogId - UUID of the catalog
   * @param {string} userId - Current user's ID (null if not authenticated)
   * @param {string} userCompanyId - Current user's company ID (null if not authenticated)
   * @returns {Promise<Object>} Catalog data if access granted
   * @throws {Error} CATALOG_NOT_FOUND - Catalog doesn't exist
   * @throws {Error} AUTHENTICATION_REQUIRED - Draft catalog requires login
   * @throws {Error} ACCESS_DENIED - User doesn't belong to catalog's company
   * @throws {Error} CATALOG_ARCHIVED - Archived catalog requires company access
   */
  async getCatalogForViewer(catalogId, userId, userCompanyId, isSuperAdmin = false) {
    try {
      console.log('[GET_CATALOG_VIEWER] Loading catalog for internal viewer:', {
        catalogId,
        userId,
        userCompanyId,
        isSuperAdmin
      });

      // Fetch catalog data using existing method
      const catalog = await this.getCatalogById(catalogId);

      if (!catalog) {
        throw new Error('CATALOG_NOT_FOUND');
      }

      // super_admin can access any catalog regardless of company ownership
      if (isSuperAdmin) {
        console.log('[GET_CATALOG_VIEWER] super_admin access granted - bypassing ownership check');
        return catalog;
      }

      // BUSINESS RULE: Internal viewer can display BOTH draft and published catalogs
      // BUT only if user has access (authenticated + same company)
      if (catalog?.status === 'draft') {
        // Draft catalogs require authentication and company access
        if (!userId || !userCompanyId) {
          throw new Error('AUTHENTICATION_REQUIRED');
        }

        if (catalog?.companyId !== userCompanyId) {
          throw new Error('ACCESS_DENIED');
        }

        console.log('[GET_CATALOG_VIEWER] Draft catalog access granted for authenticated user');
      } else if (catalog?.status === 'published') {
        // Published catalogs are accessible to anyone
        console.log('[GET_CATALOG_VIEWER] Published catalog - public access');
      } else if (catalog?.status === 'archived') {
        // Archived catalogs require company access
        if (!userId || !userCompanyId || catalog?.companyId !== userCompanyId) {
          throw new Error('CATALOG_ARCHIVED');
        }
        console.log('[GET_CATALOG_VIEWER] Archived catalog access granted for company member');
      }

      return catalog;
    } catch (error) {
      console.error('[GET_CATALOG_VIEWER] Error:', error?.message);
      throw error;
    }
  },

  /**
   * BUSINESS RULE: Embedded Viewer - PUBLISHED ONLY (backend enforcement)
   * 
   * Used by: embed-viewer (public iframe/script embeds on external websites)
   * 
   * Access Control:
   * - ONLY published catalogs are allowed
   * - Draft catalogs are REJECTED with clear error message
   * - Archived catalogs are REJECTED
   * - No authentication required (public access)
   * 
   * This ensures:
   * - Draft catalogs remain private during development
   * - Only finalized content is shown on external websites
   * - Catalog owners control when content goes public
   * - Embed viewers cannot access work-in-progress catalogs
   * 
   * Additional Validation:
   * - Verifies published catalog has accessible public images
   * - Prevents embedding catalogs with missing/broken images
   * 
   * @param {string} catalogId - UUID of the catalog
   * @returns {Promise<Object>} Catalog data if published and valid
   * @throws {Error} CATALOG_NOT_FOUND - Catalog doesn't exist
   * @throws {Error} CATALOG_NOT_PUBLISHED - Catalog is draft/archived (NOT published)
   * @throws {Error} CATALOG_IMAGES_UNAVAILABLE - Published catalog missing public images
   */
  async getCatalogForEmbed(catalogId) {
    try {
      console.log('[GET_CATALOG_EMBED] Loading catalog for embed viewer:', catalogId);

      // Fetch catalog data using existing method
      const catalog = await this.getCatalogById(catalogId);

      if (!catalog) {
        throw new Error('CATALOG_NOT_FOUND');
      }

      // BUSINESS RULE: Embed viewer MUST ONLY display published catalogs
      // This is enforced at the backend/service layer (NOT just frontend)
      if (catalog?.status !== 'published') {
        console.warn(`[GET_CATALOG_EMBED] Rejected embed access to ${catalog?.status} catalog`);
        throw new Error('CATALOG_NOT_PUBLISHED');
      }

      // Additional validation: Ensure published catalog has public images
      const hasPublicImages = catalog?.catalogPages?.some(page => page?.publicImagePath || page?.imageUrl?.startsWith('http'));
      
      if (!hasPublicImages) {
        console.error('[GET_CATALOG_EMBED] Published catalog missing public images');
        throw new Error('CATALOG_IMAGES_UNAVAILABLE');
      }

      console.log('[GET_CATALOG_EMBED] Published catalog validated for embed display');
      return catalog;
    } catch (error) {
      console.error('[GET_CATALOG_EMBED] Error:', error?.message);
      throw error;
    }
  },

  // Migrate published catalog images to public bucket (background operation)
  async migratePublishedCatalogImages(catalogId, companyId, pages) {
    try {
      console.log(`[MIGRATE] Starting migration for catalog ${catalogId}`);
      console.log(`[MIGRATE] Pages to migrate: ${pages?.length}`);

      const results = await Promise.allSettled(
        pages?.map(async (page) => {
          try {
            console.log(`[MIGRATE] Processing page ${page?.page_number}...`);
            
            const publicImage = await storageService?.copyToPublicBucket(
              page?.image_path,
              companyId
            );

            // Update database
            const { error: updateError } = await supabase
              ?.from('catalog_pages')
              ?.update({ public_image_path: publicImage?.path })
              ?.eq('id', page?.id);

            if (updateError) {
              console.error(`[MIGRATE] Failed to update page ${page?.page_number}:`, updateError);
              throw updateError;
            }

            console.log(`[MIGRATE] Successfully migrated page ${page?.page_number}`);
            return { pageNumber: page?.page_number, status: 'success' };
          } catch (err) {
            console.error(`[MIGRATE] Failed to migrate page ${page?.page_number}:`, err);
            
            // Check if this is a missing source file error
            if (err?.code === 'SOURCE_FILE_NOT_FOUND' || err?.message?.includes('Object not found')) {
              return { 
                pageNumber: page?.page_number, 
                status: 'failed', 
                error: 'Source file missing',
                errorCode: 'MISSING_SOURCE_FILE'
              };
            }
            
            return { pageNumber: page?.page_number, status: 'failed', error: err?.message };
          }
        })
      );

      const successful = results?.filter(r => r?.status === 'fulfilled' && r?.value?.status === 'success')?.length;
      const failed = results?.filter(r => r?.status === 'rejected' || r?.value?.status === 'failed')?.length;
      const missingFiles = results?.filter(
        r => (r?.status === 'fulfilled' && r?.value?.errorCode === 'MISSING_SOURCE_FILE') ||
             (r?.status === 'rejected' && r?.reason?.code === 'SOURCE_FILE_NOT_FOUND')
      )?.length;
      
      console.log(`[MIGRATE] Migration complete: ${successful} successful, ${failed} failed`);
      
      if (missingFiles > 0) {
        console.error(
          `[MIGRATE] ${missingFiles} pages have missing source files. ` +
          `These pages cannot be migrated and will not display in the embed viewer. ` +
          `The catalog should be re-uploaded.`
        );
      }
      
      return { successful, failed, missingFiles };
    } catch (error) {
      console.error('[MIGRATE] Migration error:', error);
      throw error;
    }
  },

  // Get all catalogs for the given companyId (or the current user's company if not provided)
  async getCatalogs(companyId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let effectiveCompanyId = companyId;

      if (!effectiveCompanyId) {
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('company_id')
          ?.eq('id', user?.id)
          ?.single();

        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          throw new Error('Failed to fetch user profile');
        }

        if (!profile?.company_id) {
          return [];
        }

        effectiveCompanyId = profile?.company_id;
      }

      const { data, error } = await supabase
        ?.from('catalogs')
        ?.select('*')
        ?.eq('company_id', effectiveCompanyId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching catalogs:', error?.message);
      throw error;
    }
  },

  // NEW: Lazy load page image URL on-demand
  async getPageImageUrl(imagePath) {
    try {
      if (!imagePath) return null;
      return await storageService?.getCatalogFileUrl(imagePath, 7200); // 2 hour expiry
    } catch (error) {
      console.error('Error generating page image URL:', error);
      throw error;
    }
  },

  // Create new catalog
  async createCatalog(catalogData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use companyId from catalogData if provided (e.g. super_admin with active company),
      // otherwise fall back to the profile's company_id
      let companyId = catalogData?.companyId;
      if (!companyId) {
        const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('company_id')?.eq('id', user?.id)?.single();
        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          throw new Error('Failed to fetch user profile');
        }
        if (!profile?.company_id) {
          throw new Error('User profile or company_id not found');
        }
        companyId = profile.company_id;
      }

      const snakeData = toSnakeCase({
        ...catalogData,
        companyId,
        createdBy: user?.id
      });

      const { data, error } = await supabase?.from('catalogs')?.insert(snakeData)?.select()?.single();

      if (error) {
        console.error('Error inserting catalog:', error);
        throw error;
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating catalog:', error?.message);
      throw error;
    }
  },

  // Update catalog
  async updateCatalog(catalogId, catalogData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const snakeData = toSnakeCase(catalogData);

      const { data, error } = await supabase?.from('catalogs')?.update(snakeData)?.eq('id', catalogId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating catalog:', error?.message);
      throw error;
    }
  },

  // Delete catalog
  async deleteCatalog(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('catalogs')?.delete()?.eq('id', catalogId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting catalog:', error?.message);
      throw error;
    }
  },

  // Increment catalog views
  async incrementViews(catalogId) {
    try {
      const { data: catalog } = await supabase
        ?.from('catalogs')
        ?.select('views')
        ?.eq('id', catalogId)
        ?.single();

        await supabase
        ?.from('catalogs')
        ?.update({ views: (catalog?.views || 0) + 1 })
        ?.eq('id', catalogId);
    } catch (error) {
      console.error('Error incrementing views:', error?.message);
    }
  },

  // Increment hotspot clicks
  async incrementHotspotClicks(catalogId) {
    try {
      const { data: catalog } = await supabase?.from('catalogs')?.select('hotspot_clicks')?.eq('id', catalogId)?.single();

      await supabase?.from('catalogs')?.update({ hotspot_clicks: (catalog?.hotspot_clicks || 0) + 1 })?.eq('id', catalogId);
    } catch (error) {
      console.error('Error incrementing hotspot clicks:', error?.message);
    }
  },

  // Publish catalog
  async publishCatalog(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get catalog with pages
      const { data: catalog, error: catalogError } = await supabase
        ?.from('catalogs')
        ?.select(`
          *,
          catalogPages:catalog_pages(
            id,
            page_number,
            image_path,
            public_image_path
          )
        `)
        ?.eq('id', catalogId)
        ?.single();

      if (catalogError) throw catalogError;

      console.log('[PUBLISH] Starting catalog publish:', catalogId);
      console.log('[PUBLISH] Current status:', catalog?.status);
      console.log('[PUBLISH] Total pages to process:', catalog?.catalogPages?.length);

      // Check for corrupted image paths (undefined company_id)
      const corruptedPaths = catalog?.catalogPages?.filter(
        page => page?.image_path && page?.image_path?.startsWith('undefined/')
      );

      if (corruptedPaths?.length > 0) {
        console.error('[PUBLISH] Detected corrupted image paths:', corruptedPaths?.length);
        console.error('[PUBLISH] Sample corrupted paths:', corruptedPaths?.slice(0, 3)?.map(p => p?.image_path));
        const error = new Error(
          'Cannot publish catalog: The catalog images were not uploaded correctly. ' + 'This usually happens when the user profile was not fully loaded during upload. '+ 'Please delete this catalog and upload the PDF again to fix this issue.'
        );
        error.code = 'CORRUPTED_IMAGE_PATHS';
        throw error;
      }

      // Copy images to public bucket if not already done
      const pageUpdates = await Promise.allSettled(
        (catalog?.catalogPages || [])?.map(async (page) => {
          // Skip if already has public image path
          if (page?.public_image_path) {
            console.log(`[PUBLISH] Page ${page?.page_number} already has public image, skipping`);
            return { pageId: page?.id, publicPath: page?.public_image_path, status: 'skipped' };
          }

          // Copy from private to public bucket
          if (page?.image_path) {
            try {
              console.log(`[PUBLISH] Copying page ${page?.page_number} from private to public bucket...`);
              console.log(`[PUBLISH] Source path: ${page?.image_path}`);
              
              const publicImage = await storageService?.copyToPublicBucket(
                page?.image_path,
                catalog?.company_id
              );

              console.log(`[PUBLISH] Successfully copied page ${page?.page_number}`);
              console.log(`[PUBLISH] Public path: ${publicImage?.path}`);
              console.log(`[PUBLISH] Public URL: ${publicImage?.publicUrl}`);

              // Update page with public image path
              const { error: updateError } = await supabase
                ?.from('catalog_pages')
                ?.update({ public_image_path: publicImage?.path })
                ?.eq('id', page?.id);

              if (updateError) {
                console.error(`[PUBLISH] Error updating page ${page?.page_number} with public image path:`, updateError);
                throw updateError;
              }

              console.log(`[PUBLISH] Database updated for page ${page?.page_number}`);
              return { pageId: page?.id, publicPath: publicImage?.path, status: 'success' };
            } catch (err) {
              console.error(`[PUBLISH] CRITICAL ERROR copying image for page ${page?.page_number}:`, err);
              console.error(`[PUBLISH] Error details:`, {
                message: err?.message,
                code: err?.code,
                statusCode: err?.statusCode,
                imagePath: page?.image_path
              });
              
              // Check if this is a missing source file error
              if (err?.code === 'SOURCE_FILE_NOT_FOUND' || err?.message?.includes('Object not found')) {
                return { 
                  pageId: page?.id, 
                  pageNumber: page?.page_number,
                  error: 'Source file missing from storage', 
                  errorCode: 'MISSING_SOURCE_FILE',
                  status: 'failed' 
                };
              }
              
              // Return error info instead of null so we can track failures
              return { 
                pageId: page?.id, 
                pageNumber: page?.page_number,
                error: err?.message, 
                status: 'failed' 
              };
            }
          }

          console.warn(`[PUBLISH] Page ${page?.page_number} has no image_path, skipping`);
          return { pageId: page?.id, pageNumber: page?.page_number, status: 'no_image' };
        })
      );

      // Log results
      const successful = pageUpdates?.filter(r => r?.status === 'fulfilled' && r?.value?.status === 'success');
      const failed = pageUpdates?.filter(r => r?.status === 'rejected' || r?.value?.status === 'failed');
      const skipped = pageUpdates?.filter(r => r?.status === 'fulfilled' && r?.value?.status === 'skipped');
      const missingSourceFiles = failed?.filter(r => r?.value?.errorCode === 'MISSING_SOURCE_FILE');
      
      console.log('[PUBLISH] Results summary:');
      console.log(`  - Successful: ${successful?.length}`);
      console.log(`  - Failed: ${failed?.length}`);
      console.log(`  - Skipped: ${skipped?.length}`);
      console.log(`  - Missing source files: ${missingSourceFiles?.length}`);
      
      if (failed?.length > 0) {
        console.error('[PUBLISH] Failed pages:', failed?.map(f => ({
          page: f?.value?.pageNumber || f?.reason,
          error: f?.value?.error || f?.reason
        })));
      }
      
      // If all pages failed due to missing source files, throw descriptive error
      if (missingSourceFiles?.length > 0 && missingSourceFiles?.length === catalog?.catalogPages?.length) {
        const error = new Error(
          `Cannot publish catalog: All page images are missing from storage. ` +
          `This catalog needs to be re-uploaded. Please delete this catalog and upload the PDF again.`
        );
        error.code = 'ALL_SOURCE_FILES_MISSING';
        throw error;
      }
      
      // If some pages failed, warn but continue
      if (failed?.length > 0) {
        console.warn(
          `[PUBLISH] ${failed?.length} pages failed to copy. ` +
          `Catalog will be published but some pages may not display correctly.`
        );
      }

      // Copy original PDF to public bucket for download in embed
      let publicPdfPath = null;
      if (catalog?.original_pdf_path) {
        try {
          console.log('[PUBLISH] Copying original PDF to public bucket...');
          const publicPdf = await storageService?.copyPdfToPublicBucket(
            catalog?.original_pdf_path,
            catalog?.company_id
          );
          publicPdfPath = publicPdf?.path;
          console.log('[PUBLISH] PDF copied to public bucket:', publicPdfPath);
        } catch (pdfErr) {
          console.warn('[PUBLISH] Failed to copy PDF to public bucket (non-fatal):', pdfErr?.message);
        }
      }

      // Update catalog status to published (lowercase, as per requirement)
      const { data, error } = await supabase
        ?.from('catalogs')
        ?.update({
          status: 'published',
          published_at: new Date()?.toISOString(),
          ...(publicPdfPath ? { public_pdf_path: publicPdfPath } : {})
        })
        ?.eq('id', catalogId)
        ?.select()
        ?.single();

      if (error) throw error;
      
      console.log('[PUBLISH] Catalog status updated to "published" (lowercase)');
      console.log('[PUBLISH] Published at:', data?.published_at);
      return toCamelCase(data);
    } catch (error) {
      console.error('[PUBLISH] Fatal error publishing catalog:', error);
      throw error;
    }
  },

  // Unpublish catalog (revert to DRAFT)
  async unpublishCatalog(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('[UNPUBLISH] Reverting catalog to draft:', catalogId);

      // Update catalog status to draft (lowercase, as per requirement)
      const { data, error } = await supabase
        ?.from('catalogs')
        ?.update({ 
          status: 'draft',
          published_at: null
        })
        ?.eq('id', catalogId)
        ?.select()
        ?.single();

      if (error) throw error;
      
      console.log('[UNPUBLISH] Catalog status updated to "draft" (lowercase)');
      console.log('[UNPUBLISH] Published_at cleared');
      return toCamelCase(data);
    } catch (error) {
      console.error('[UNPUBLISH] Error unpublishing catalog:', error);
      throw error;
    }
  },

  // Archive catalog (only allowed from draft status)
  async archiveCatalog(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('[ARCHIVE] Archiving catalog:', catalogId);

      // Validate current status — only draft catalogs can be archived
      const { data: current, error: fetchError } = await supabase
        ?.from('catalogs')
        ?.select('status')
        ?.eq('id', catalogId)
        ?.single();

      if (fetchError) throw fetchError;

      if (current?.status !== 'draft') {
        const err = new Error('CATALOG_NOT_DRAFT');
        err.code = 'CATALOG_NOT_DRAFT';
        console.error('[ARCHIVE] Cannot archive — catalog is not in draft status:', current?.status);
        throw err;
      }

      const { data, error } = await supabase
        ?.from('catalogs')
        ?.update({ status: 'archived' })
        ?.eq('id', catalogId)
        ?.select()
        ?.single();

      if (error) throw error;

      console.log('[ARCHIVE] Catalog archived successfully:', catalogId);

      // Log activity (non-fatal)
      try {
        const { activityService } = await import('./activityService');
        await activityService?.logActivity({
          type: 'archive',
          action: 'archived',
          catalogId,
          catalogTitle: data?.title
        });
      } catch (logErr) {
        console.warn('[ARCHIVE] Activity log failed (non-fatal):', logErr?.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('[ARCHIVE] Error archiving catalog:', error?.message);
      throw error;
    }
  },

  // Restore catalog from archived to draft
  async restoreCatalog(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('[RESTORE] Restoring catalog to draft:', catalogId);

      const { data, error } = await supabase
        ?.from('catalogs')
        ?.update({ status: 'draft' })
        ?.eq('id', catalogId)
        ?.select()
        ?.single();

      if (error) throw error;

      console.log('[RESTORE] Catalog restored to draft:', catalogId);

      // Log activity (non-fatal)
      try {
        const { activityService } = await import('./activityService');
        await activityService?.logActivity({
          type: 'restore',
          action: 'restored',
          catalogId,
          catalogTitle: data?.title
        });
      } catch (logErr) {
        console.warn('[RESTORE] Activity log failed (non-fatal):', logErr?.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('[RESTORE] Error restoring catalog:', error?.message);
      throw error;
    }
  },

  // Rename catalog
  async renameCatalog(catalogId, newTitle) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('catalogs')
        ?.update({ title: newTitle })
        ?.eq('id', catalogId)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error renaming catalog:', error?.message);
      throw error;
    }
  },

  // Create catalog pages from PDF
  async createCatalogPages(catalogId, pagesData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const snakeData = pagesData?.map(page => toSnakeCase({
        catalogId,
        ...page
      }));

      const { data, error } = await supabase?.from('catalog_pages')?.insert(snakeData)?.select();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating catalog pages:', error?.message);
      throw error;
    }
  },

  // Get catalog pages
  async getCatalogPages(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('catalog_pages')?.select('*')?.eq('catalog_id', catalogId)?.order('page_number', { ascending: true });

      if (error) throw error;
      
      // Generate fresh signed URLs for each page
      const pagesWithUrls = await Promise.all(
        data?.map(async (page) => {
          let imageUrl = page?.image_url;
          
          // If we have an image_path, generate a fresh signed URL
          if (page?.image_path) {
            try {
              imageUrl = await storageService?.getCatalogFileUrl(page?.image_path, 7200); // 2 hour expiry
            } catch (err) {
              console.error('Error generating signed URL for page:', page?.page_number, err);
            }
          }
          
          return {
            ...page,
            image_url: imageUrl
          };
        })
      );
      
      return toCamelCase(pagesWithUrls);
    } catch (error) {
      console.error('Error fetching catalog pages:', error?.message);
      throw error;
    }
  },

  // Share catalog with email
  async shareCatalog(catalogId, recipientEmail, recipientName, message) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get catalog and user details
      const [catalog, profile] = await Promise.all([
        this.getCatalogById(catalogId),
        (async () => {
          const { userService } = await import('./userService');
          return userService?.getCurrentProfile();
        })()
      ]);

      if (!catalog) throw new Error('Catalog not found');
      if (!profile?.companyId) throw new Error('Company not found');

      // Record the share in database
      const { data: shareData, error: shareError } = await supabase
        ?.from('catalog_shares')
        ?.insert({
          catalog_id: catalogId,
          shared_by: user?.id,
          shared_with_email: recipientEmail,
          shared_with_name: recipientName,
          company_id: profile?.companyId,
          message: message
        })
        ?.select()
        ?.single();

      if (shareError) throw shareError;

      // Send email notification
      try {
        const { emailService } = await import('./emailService');
        const catalogUrl = `${window.location?.origin}/catalog-viewer/${catalogId}`;
        
        await emailService?.sendCatalogShared({
          recipientEmail: recipientEmail,
          recipientName: recipientName || 'there',
          sharedBy: profile?.fullName || 'A team member',
          catalogTitle: catalog?.title,
          catalogDescription: message,
          catalogUrl: catalogUrl
        });
      } catch (emailError) {
        console.error('Failed to send catalog share email:', emailError?.message);
        // Don't throw - share was recorded successfully
      }

      return toCamelCase(shareData);
    } catch (error) {
      console.error('Error sharing catalog:', error?.message);
      throw error;
    }
  },

  // Get catalog shares
  async getCatalogShares(catalogId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('catalog_shares')
        ?.select('*, user_profiles!catalog_shares_shared_by_fkey(full_name)')
        ?.eq('catalog_id', catalogId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching catalog shares:', error?.message);
      throw error;
    }
  },

  // Get first 4 page preview image URLs for a list of catalog IDs
  async getCatalogPreviewImages(catalogIds) {
    if (!catalogIds?.length) return {};
    try {
      const { data: pages, error } = await supabase
        ?.from('catalog_pages')
        ?.select('catalog_id, page_number, image_path, public_image_path')
        ?.in('catalog_id', catalogIds)
        ?.order('page_number', { ascending: true });

      if (error) throw error;

      // Group first 4 pages by catalog_id (already ordered by page_number)
      const pagesByCatalog = {};
      for (const page of pages || []) {
        if (!pagesByCatalog[page.catalog_id]) pagesByCatalog[page.catalog_id] = [];
        if (pagesByCatalog[page.catalog_id].length < 4) {
          pagesByCatalog[page.catalog_id].push(page);
        }
      }

      const result = {};
      await Promise.all(
        Object.entries(pagesByCatalog).map(async ([catalogId, catalogPages]) => {
          const selected = catalogPages;
          const urls = await Promise.all(
            selected.map(async (page) => {
              if (page.public_image_path) {
                return storageService?.getPublicCatalogImageUrl(page.public_image_path);
              } else if (page.image_path) {
                try {
                  return await storageService?.getCatalogFileUrl(page.image_path, 7200);
                } catch {
                  return null;
                }
              }
              return null;
            })
          );
          result[catalogId] = urls.filter(Boolean);
        })
      );

      return result;
    } catch (error) {
      console.error('Error fetching catalog preview images:', error);
      return {};
    }
  },

  // Update keywords for a catalog page
  async updatePageKeywords(pageId, keywords) {
    try {
      const { data, error } = await supabase
        ?.from('catalog_pages')
        ?.update({ keywords })
        ?.eq('id', pageId)
        ?.select('id, keywords')
        ?.single();
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating page keywords:', error?.message);
      throw error;
    }
  }
};